/**
 * Major world events data (1940-2025)
 * Curated list of significant events that affected humanity
 */

export interface WorldEvent {
    year: number;
    title: string;
    description: string;
    category: 'technology' | 'politics' | 'health' | 'culture' | 'science' | 'disaster';
}

export const worldEvents: WorldEvent[] = [
    // 1940s
    { year: 1945, title: 'End of World War II', description: 'The deadliest conflict in history concluded', category: 'politics' },
    { year: 1945, title: 'United Nations founded', description: 'Global organization for peace established', category: 'politics' },
    { year: 1948, title: 'Universal Declaration of Human Rights', description: 'Fundamental rights proclaimed globally', category: 'politics' },

    // 1950s
    { year: 1953, title: 'DNA structure discovered', description: 'Watson and Crick revealed the double helix', category: 'science' },
    { year: 1957, title: 'Sputnik launched', description: 'First artificial satellite began the Space Age', category: 'science' },

    // 1960s
    { year: 1961, title: 'First human in space', description: 'Yuri Gagarin orbited Earth', category: 'science' },
    { year: 1963, title: '"I Have a Dream" speech', description: 'Martin Luther King Jr. addressed civil rights', category: 'culture' },
    { year: 1969, title: 'Moon landing', description: 'Apollo 11 achieved humanity\'s first steps on the Moon', category: 'science' },

    // 1970s
    { year: 1971, title: 'First email sent', description: 'Ray Tomlinson sent the first networked message', category: 'technology' },
    { year: 1973, title: 'First mobile phone call', description: 'Martin Cooper made the first handheld cellular call', category: 'technology' },

    // 1980s
    { year: 1981, title: 'IBM PC released', description: 'Personal computing became mainstream', category: 'technology' },
    { year: 1989, title: 'Berlin Wall fell', description: 'The Cold War began to end', category: 'politics' },
    { year: 1989, title: 'World Wide Web invented', description: 'Tim Berners-Lee created the foundation of the modern internet', category: 'technology' },

    // 1990s
    { year: 1990, title: 'Hubble Space Telescope launched', description: 'Our window to the deep universe opened', category: 'science' },
    { year: 1991, title: 'Soviet Union dissolved', description: 'End of the Cold War era', category: 'politics' },
    { year: 1994, title: 'Nelson Mandela elected', description: 'First Black president of South Africa, end of apartheid', category: 'politics' },
    { year: 1997, title: 'Dolly the sheep cloned', description: 'First mammal cloned from an adult cell', category: 'science' },
    { year: 1998, title: 'Google founded', description: 'The search engine that changed how we find information', category: 'technology' },

    // 2000s
    { year: 2001, title: 'Wikipedia launched', description: 'Free knowledge for everyone', category: 'technology' },
    { year: 2001, title: 'September 11 attacks', description: 'Attacks that reshaped global security', category: 'politics' },
    { year: 2003, title: 'Human Genome Project completed', description: 'Our entire genetic code was mapped', category: 'science' },
    { year: 2004, title: 'Facebook launched', description: 'Social media transformed human connection', category: 'technology' },
    { year: 2007, title: 'iPhone released', description: 'Smartphones became essential to daily life', category: 'technology' },
    { year: 2008, title: 'Global financial crisis', description: 'Worst economic downturn since the Great Depression', category: 'politics' },

    // 2010s
    { year: 2010, title: 'Instagram launched', description: 'Visual social sharing went mainstream', category: 'technology' },
    { year: 2011, title: 'Arab Spring', description: 'Pro-democracy protests swept the Middle East', category: 'politics' },
    { year: 2012, title: 'Higgs boson discovered', description: 'The "God particle" was confirmed at CERN', category: 'science' },
    { year: 2015, title: 'Paris Climate Agreement', description: '195 nations committed to fight climate change', category: 'politics' },
    { year: 2016, title: 'AlphaGo defeats world champion', description: 'AI achieved superhuman performance in Go', category: 'technology' },
    { year: 2016, title: 'Gravitational waves detected', description: 'Einstein\'s century-old prediction confirmed', category: 'science' },
    { year: 2017, title: 'First image of a black hole', description: 'Humanity saw the unseeable', category: 'science' },

    // 2020s
    { year: 2020, title: 'COVID-19 pandemic', description: 'Global pandemic changed how the world lives and works', category: 'health' },
    { year: 2020, title: 'mRNA vaccines developed', description: 'Revolutionary vaccine technology deployed in record time', category: 'science' },
    { year: 2021, title: 'James Webb Space Telescope launched', description: 'Most powerful space telescope began observing the universe', category: 'science' },
    { year: 2022, title: 'ChatGPT released', description: 'AI became conversational and accessible to everyone', category: 'technology' },
    { year: 2023, title: 'First malaria vaccine approved', description: 'Breakthrough in fighting one of humanity\'s oldest diseases', category: 'health' },
];

/**
 * Get events that occurred during a person's lifetime
 */
export function getEventsInLifetime(birthYear: number, currentYear: number = new Date().getFullYear()): WorldEvent[] {
    return worldEvents.filter(event => event.year >= birthYear && event.year <= currentYear);
}

/**
 * Get a curated selection of the most significant events
 */
export function getTopEvents(birthYear: number, count: number = 5): WorldEvent[] {
    const events = getEventsInLifetime(birthYear);
    // Return a diverse selection of categories
    const selected: WorldEvent[] = [];
    const categories = ['technology', 'politics', 'health', 'science', 'culture', 'disaster'];

    for (const category of categories) {
        const categoryEvents = events.filter(e => e.category === category);
        if (categoryEvents.length > 0 && selected.length < count) {
            // Pick the most recent from this category
            selected.push(categoryEvents[categoryEvents.length - 1]);
        }
    }

    // Fill remaining slots with most recent events
    while (selected.length < count && events.length > selected.length) {
        const remaining = events.filter(e => !selected.includes(e));
        if (remaining.length > 0) {
            selected.push(remaining[remaining.length - 1]);
        } else {
            break;
        }
    }

    return selected.sort((a, b) => b.year - a.year);
}
